import React, { useCallback, useEffect, useMemo, useState } from "react";
import CallCenterModal from "./listing/CallCenterModal";
import { PDFViewer, pdf } from "@react-pdf/renderer";
import { toast } from "react-toastify";
import useCallCenterT from "../../hooks/useCallCenterT";
import PaymentProofPdfDocument from "./PaymentProofPdfDocument";
import {
  PAYMENT_PROOF_FIELD_KEYS,
  buildWhatsAppMessage,
  formatPaymentProofDateTime,
} from "./paymentProofUtils";
import {
  checkTicketWhatsAppPhone,
  downloadTicketPaymentProof,
  getTicketPaymentProof,
  listTicketWhatsAppPhones,
  saveTicketPaymentProof,
  saveTicketPaymentProofDraft,
  sendTicketPaymentProofWhatsApp,
} from "../../service/CallCenterTickets";
import { resolvePaymentProofTemplateImages } from "../../service/CallCenterPaymentProofTemplate";

const PaymentProofPanel = ({ ticketId, refreshKey = 0, onSaved, showHint = true }) => {
  const t = useCallCenterT();
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState("");
  const [saving, setSaving] = useState(false);
  const [sending, setSending] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [template, setTemplate] = useState({});
  const [fieldData, setFieldData] = useState({});
  const [generated, setGenerated] = useState(false);
  const [fileName, setFileName] = useState("");
  const [generatedAt, setGeneratedAt] = useState(null);
  const [phoneNumbers, setPhoneNumbers] = useState([]);
  const [selectedPhone, setSelectedPhone] = useState("");
  const [whatsappMessage, setWhatsappMessage] = useState("");
  const [phoneCheck, setPhoneCheck] = useState({ status: "idle", onWhatsApp: null });
  const [previewTemplate, setPreviewTemplate] = useState({});

  const fieldLabelsEn = template?.fieldLabelsEn || {};
  const ready = Boolean(ticketId) && !loading && !loadError;

  const loadContext = useCallback(async () => {
    if (!ticketId) {
      setTemplate({});
      setFieldData({});
      setGenerated(false);
      setLoadError("");
      return;
    }
    try {
      setLoading(true);
      setLoadError("");
      const context = await getTicketPaymentProof(ticketId);
      let phones = [];
      try {
        const phonesResponse = await listTicketWhatsAppPhones(ticketId);
        phones = Array.isArray(phonesResponse)
          ? phonesResponse.map((item) => item?.phoneNumber || item).filter(Boolean)
          : [];
      } catch (phoneError) {
        toast.error(phoneError?.message || t("ccWhatsAppCheckFailed"));
      }

      const nextTemplate = context?.template || {};
      const nextFieldData = context?.fieldData || {};
      setTemplate(nextTemplate);
      setFieldData(nextFieldData);
      setGenerated(Boolean(context?.generated));
      setFileName(context?.fileName || "");
      setGeneratedAt(context?.generatedAt || null);
      setWhatsappMessage(buildWhatsAppMessage(nextTemplate, nextFieldData, ""));
      setPhoneNumbers(phones);
      setSelectedPhone(phones[0] || "");
      setPhoneCheck({ status: "idle", onWhatsApp: null });
    } catch (error) {
      setLoadError(error?.message || t("ccPaymentProofLoadFailed"));
      setTemplate({});
      setFieldData({});
      setGenerated(false);
      setPhoneNumbers([]);
      setSelectedPhone("");
    } finally {
      setLoading(false);
    }
  }, [ticketId, t]);

  useEffect(() => {
    loadContext();
  }, [loadContext, refreshKey]);

  useEffect(() => {
    let cancelled = false;
    const resolvePreviewTemplate = async () => {
      try {
        const resolved = await resolvePaymentProofTemplateImages(template);
        if (!cancelled) {
          setPreviewTemplate(resolved);
        }
      } catch {
        if (!cancelled) {
          setPreviewTemplate(template);
        }
      }
    };
    resolvePreviewTemplate();
    return () => {
      cancelled = true;
    };
  }, [template]);

  useEffect(() => {
    if (!ticketId || !selectedPhone) {
      setPhoneCheck({ status: "idle", onWhatsApp: null });
      return undefined;
    }
    let cancelled = false;
    const verifyPhone = async () => {
      try {
        setPhoneCheck({ status: "checking", onWhatsApp: null });
        const result = await checkTicketWhatsAppPhone(ticketId, selectedPhone);
        if (!cancelled) {
          setPhoneCheck({ status: "done", onWhatsApp: Boolean(result?.onWhatsApp) });
        }
      } catch {
        if (!cancelled) {
          setPhoneCheck({ status: "error", onWhatsApp: null });
        }
      }
    };
    verifyPhone();
    return () => {
      cancelled = true;
    };
  }, [ticketId, selectedPhone]);

  const handleFieldChange = (key, value) => {
    setFieldData((current) => {
      const next = { ...current, [key]: value };
      setWhatsappMessage(buildWhatsAppMessage(template, next, ""));
      return next;
    });
  };

  const generatePdfBlob = async () => {
    const resolvedTemplate = await resolvePaymentProofTemplateImages(template);
    const blob = await pdf(
      <PaymentProofPdfDocument template={resolvedTemplate} fieldData={fieldData} />
    ).toBlob();
    return blob;
  };

  const handleSaveDraft = async () => {
    if (!ready) return;
    try {
      setSaving(true);
      await saveTicketPaymentProofDraft(ticketId, fieldData);
      toast.success(t("ccPaymentProofDraftSaved"));
    } catch (error) {
      toast.error(error?.message || t("ccPaymentProofDraftSaveFailed"));
    } finally {
      setSaving(false);
    }
  };

  const handleSaveAndDownload = async () => {
    if (!ready) return;
    try {
      setSaving(true);
      const blob = await generatePdfBlob();
      const resolvedFileName = fileName || "clearance-certificate.pdf";
      const file = new File([blob], resolvedFileName, { type: "application/pdf" });
      await saveTicketPaymentProof(ticketId, { file, fieldData });
      const objectUrl = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = objectUrl;
      link.download = resolvedFileName;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(objectUrl);
      toast.success(t("ccPaymentProofSaved"));
      setGenerated(true);
      setGeneratedAt(new Date().toISOString());
      setFileName(resolvedFileName);
      if (onSaved) onSaved();
    } catch (error) {
      toast.error(error?.message || t("ccPaymentProofSaveFailed"));
    } finally {
      setSaving(false);
    }
  };

  const handleDownloadSaved = async () => {
    if (!ready || !generated) return;
    try {
      setDownloading(true);
      await downloadTicketPaymentProof(ticketId, fileName || "clearance-certificate.pdf");
    } catch (error) {
      toast.error(error?.message || t("ccPaymentProofDownloadFailed"));
    } finally {
      setDownloading(false);
    }
  };

  const handleSendWhatsApp = async () => {
    if (!ready) return;
    if (!selectedPhone) {
      toast.error(t("ccPaymentProofPhoneRequired"));
      return;
    }
    if (phoneCheck.status === "done" && phoneCheck.onWhatsApp === false) {
      toast.error(t("ccWhatsAppNumberInvalidHint"));
      return;
    }
    try {
      setSending(true);
      if (!generated) {
        const blob = await generatePdfBlob();
        const resolvedFileName = fileName || "clearance-certificate.pdf";
        const file = new File([blob], resolvedFileName, { type: "application/pdf" });
        await saveTicketPaymentProof(ticketId, { file, fieldData });
        setGenerated(true);
        setGeneratedAt(new Date().toISOString());
        setFileName(resolvedFileName);
      }
      await sendTicketPaymentProofWhatsApp(ticketId, {
        phoneNumber: selectedPhone,
        message: whatsappMessage,
      });
      toast.success(t("ccPaymentProofWhatsAppSent"));
      if (onSaved) onSaved();
    } catch (error) {
      toast.error(error?.message || t("ccPaymentProofWhatsAppFailed"));
    } finally {
      setSending(false);
    }
  };

  const previewDocument = useMemo(
    () => <PaymentProofPdfDocument template={previewTemplate} fieldData={fieldData} />,
    [previewTemplate, fieldData]
  );

  if (loading) {
    return <p className="text-sm text-gray-500">{t("ccLoading")}</p>;
  }

  if (loadError) {
    return (
      <div className="rounded-md border border-red-200 bg-red-50 px-3 py-3 text-sm text-red-700">
        <p>{loadError}</p>
        <button
          type="button"
          onClick={loadContext}
          className="mt-2 rounded-md border border-red-300 px-3 py-1 text-sm hover:bg-red-100"
        >
          {t("ccReset")}
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {showHint && (
        <p className="text-sm text-gray-600">{t("ccPaymentProofHint")}</p>
      )}

      {generated && (
        <div className="rounded-md border border-green-200 bg-green-50 px-3 py-2 text-sm text-green-800">
          {t("ccPaymentProofGeneratedAt")}: {formatPaymentProofDateTime(generatedAt)}
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        {PAYMENT_PROOF_FIELD_KEYS.map((key) => (
          <label key={key} className="block text-sm">
            <span className="font-medium text-gray-700">{fieldLabelsEn[key] || key}</span>
            <input
              type="text"
              value={fieldData[key] ?? ""}
              onChange={(event) => handleFieldChange(key, event.target.value)}
              disabled={!ready || saving || sending}
              className="mt-1 w-full rounded-md border border-gray-300 px-3 py-2 text-sm disabled:bg-gray-100"
            />
          </label>
        ))}
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">
          {t("ccPaymentProofWhatsAppMessage")}
        </label>
        <textarea
          value={whatsappMessage}
          onChange={(event) => setWhatsappMessage(event.target.value)}
          rows={3}
          disabled={!ready || saving || sending}
          className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm disabled:bg-gray-100"
        />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <label className="block text-sm">
          <span className="font-medium text-gray-700">{t("ccPaymentProofWhatsAppPhone")}</span>
          {phoneNumbers.length > 0 ? (
            <select
              value={selectedPhone}
              onChange={(event) => setSelectedPhone(event.target.value)}
              disabled={!ready || saving || sending}
              className="mt-1 w-full rounded-md border border-gray-300 px-3 py-2 text-sm disabled:bg-gray-100"
            >
              {phoneNumbers.map((phone) => (
                <option key={phone} value={phone}>
                  {phone}
                </option>
              ))}
            </select>
          ) : (
            <input
              type="text"
              value={selectedPhone}
              onChange={(event) => setSelectedPhone(event.target.value)}
              placeholder={t("ccPhoneNumber")}
              disabled={!ready || saving || sending}
              className="mt-1 w-full rounded-md border border-gray-300 px-3 py-2 text-sm disabled:bg-gray-100"
            />
          )}
          {phoneCheck.status === "checking" && (
            <span className="mt-1 block text-xs text-gray-500">{t("ccWhatsAppCheckingNumber")}</span>
          )}
          {phoneCheck.status === "done" && phoneCheck.onWhatsApp === true && (
            <span className="mt-1 block text-xs text-green-700">{t("ccWhatsAppNumberValid")}</span>
          )}
          {phoneCheck.status === "done" && phoneCheck.onWhatsApp === false && (
            <span className="mt-1 block text-xs text-red-700">{t("ccWhatsAppNumberInvalidHint")}</span>
          )}
        </label>
      </div>

      <div className="flex flex-wrap gap-2">
        <button
          type="button"
          onClick={() => setPreviewOpen(true)}
          disabled={!ready || saving || sending}
          className="rounded-md border border-gray-300 px-3 py-2 text-sm hover:bg-gray-50 disabled:opacity-60"
        >
          {t("ccPaymentProofPreview")}
        </button>
        <button
          type="button"
          onClick={handleSaveDraft}
          disabled={!ready || saving || sending}
          className="rounded-md border border-gray-300 px-3 py-2 text-sm hover:bg-gray-50 disabled:opacity-60"
        >
          {saving ? t("ccSaving") : t("ccPaymentProofSaveDraft")}
        </button>
        <button
          type="button"
          onClick={handleSaveAndDownload}
          disabled={!ready || saving || sending}
          className="rounded-md bg-blue-600 px-3 py-2 text-sm text-white hover:bg-blue-700 disabled:opacity-60"
        >
          {saving ? t("ccSaving") : t("ccPaymentProofSaveDownload")}
        </button>
        {generated && (
          <button
            type="button"
            onClick={handleDownloadSaved}
            disabled={!ready || downloading || saving || sending}
            className="rounded-md border border-gray-300 px-3 py-2 text-sm hover:bg-gray-50 disabled:opacity-60"
          >
            {downloading ? t("ccProcessing") : t("ccPaymentProofDownloadSaved")}
          </button>
        )}
        <button
          type="button"
          onClick={handleSendWhatsApp}
          disabled={!ready || sending || saving}
          className="rounded-md bg-green-600 px-3 py-2 text-sm text-white hover:bg-green-700 disabled:opacity-60"
        >
          {sending ? t("ccProcessing") : t("ccPaymentProofSendWhatsApp")}
        </button>
      </div>

      <CallCenterModal
        open={previewOpen}
        onClose={() => setPreviewOpen(false)}
        title={t("ccPaymentProofPreview")}
        maxWidth="5xl"
        maxHeight="min(85vh,800px)"
        zIndexClass="z-[220]"
        closeLabel={t("ccClose")}
        contentVariant="none"
        bodyClassName="min-h-0 flex-1 overflow-hidden p-0"
      >
        <div className="flex h-[min(70vh,720px)] min-h-[320px] flex-col">
          <PDFViewer width="100%" height="100%" showToolbar>
            {previewDocument}
          </PDFViewer>
        </div>
      </CallCenterModal>
    </div>
  );
};

export default PaymentProofPanel;
